import { useQuery } from '@tanstack/react-query'

import { type AppointmentTypeId, type ClinicId, dataOrThrow } from '@snootsvet/shared/portal'

import { usePortalApi } from '@/customer/hooks/use-api'

/**
 * React Query key prefix for available-slots reads. Exported so
 * mutation hooks (booking, rescheduling) can invalidate every slot
 * range at once without having to know the specific clinic /
 * appointment-type / date-range tuple that's currently mounted.
 */
export const availableSlotsQueryKey = ['available-slots'] as const

type UseGetAvailableSlotsInput = {
  /** Inclusive start of the range, as a YYYY-MM-DD clinic-local date. */
  startDate: string
  /** Exclusive end of the range, as a YYYY-MM-DD clinic-local date. */
  endDate: string
} & ({ appointmentId: string } | { appointmentTypeId: AppointmentTypeId; clinicId: ClinicId })

/**
 * Loads bookable slots for the portal booking flow via
 * `GET /api/portal/appointments/available-slots`.
 *
 * Accepts a nullable input so callers can mount the hook before URL
 * params are validated; the query stays disabled until `input` is
 * populated.
 */
export function useGetAvailableSlots(
  input: UseGetAvailableSlotsInput | null,
  options: { enabled?: boolean } = {}
) {
  const api = usePortalApi()

  return useQuery({
    queryKey: [
      ...availableSlotsQueryKey,
      input && 'appointmentId' in input ? input.appointmentId : input?.appointmentTypeId,
      input && 'clinicId' in input ? input.clinicId : null,
      input?.startDate,
      input?.endDate,
    ] as const,
    enabled: (options.enabled ?? true) && input !== null,
    queryFn: async () => {
      if (!input) throw new Error('useGetAvailableSlots: input is null')
      return dataOrThrow(await api.get('/appointments/available-slots', { query: input }))
    },
  })
}
